import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState } from '@/components/EmptyState';
import { StatusBadge } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { roleLabel } from '@/i18n/roleLabel';
import { OFFICE_ROLES, type OfficeRole, type OneTimeLink } from '@/api/types';
import { ROLE_HELP, describeStaffError } from './staff';
import { OneTimeLinkPanel } from './List';

/**
 * ADR-084 — one employee: who, which role (and what it allows), which
 * warehouse, whether access is active - and what THIS administrator may do.
 * Every action is re-checked by the server; refusals are explained in words.
 */
export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const { t } = useLanguage();
  const { state, hasPermission } = useAuth();
  const result = useApi(() => endpoints.user(id), [id]);
  const [role, setRole] = useState<OfficeRole | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [link, setLink] = useState<(OneTimeLink & { forWhom: string }) | null>(null);
  const self = state.status === 'authenticated' ? state.self : null;
  const canWrite = hasPermission('USERS.WRITE');

  async function act(fn: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      setNote(done);
      result.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') {
    return (
      <div>
        <p className="breadcrumb"><Link to="/users">← {t('nav.users')}</Link></p>
        <div className="notice" role="alert">{t('staff.notFoundOrOutside')}</div>
      </div>
    );
  }
  const m = result.data;
  const name = m.display_name ?? m.email ?? '';
  const isSelf = m.id === self?.membership_id;
  const chosenRole = (role || m.role) as OfficeRole;
  const whLabel = (m.warehouse_name as string | null | undefined) ?? (m.warehouse_id ? t('staff.oneWarehouse') : t('settings.orgWide'));

  return (
    <div>
      <p className="breadcrumb"><Link to="/users">← {t('nav.users')}</Link></p>
      <div className="page-header">
        <div>
          <h1>{name}</h1>
          <div className="sub">{m.email}</div>
        </div>
        <StatusBadge value={m.state} />
      </div>

      {link && <OneTimeLinkPanel kind="recover" link={link} onClose={() => setLink(null)} />}
      {note && <p className="ok-note" role="status">{note}</p>}
      {error !== null && <div className="error-banner" role="alert">{describeStaffError(t, error) ?? t('staff.err.checkFields')}</div>}

      <section className="card" aria-labelledby="acc-title">
        <h2 id="acc-title" className="card-title">{t('staff.accessTitle')}</h2>
        <dl className="kv">
          <dt>{t('staff.role')}</dt>
          <dd>{roleLabel(t, m.role)}</dd>
          <dt>{t('staff.access')}</dt>
          <dd>{whLabel}</dd>
          <dt>{t('common.status')}</dt>
          <dd>{t(`staff.state.${m.state}` as 'staff.state.ACTIVE')}</dd>
        </dl>
        <p className="field-hint">{t(ROLE_HELP[m.role])}</p>
      </section>

      {canWrite && isSelf && <p className="notice">{t('staff.selfNote')}</p>}
      {canWrite && !isSelf && (
        <section className="card wh-form" aria-labelledby="actions-title">
          <h2 id="actions-title" className="card-title">{t('staff.actionsTitle')}</h2>
          {m.state !== 'REVOKED' && (
            <div className="field-row">
              <label htmlFor="role-select">{t('staff.changeRole')}</label>
              <div className="code-field">
                <select id="role-select" value={chosenRole} onChange={(e) => setRole(e.target.value as OfficeRole)} disabled={busy}>
                  {OFFICE_ROLES.map((r) => (
                    <option key={r} value={r}>{roleLabel(t, r)}</option>
                  ))}
                </select>
                <button type="button" disabled={busy || chosenRole === m.role} onClick={() => void act(() => endpoints.assignRole({ membershipId: m.id, role: chosenRole }), t('staff.roleChanged'))}>
                  {t('common.save')}
                </button>
              </div>
              <p className="field-hint">{t(ROLE_HELP[chosenRole])}</p>
            </div>
          )}
          <div className="staff-actions">
            {m.state === 'ACTIVE' && (
              <>
                <button type="button" disabled={busy} onClick={() => void act(() => endpoints.recoveryLink(m.id).then((l) => setLink({ ...l, forWhom: name })), t('staff.recoveryCreated'))}>
                  {t('staff.recoveryLink')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => window.confirm(t('staff.confirmSuspend', { name })) && void act(() => endpoints.setUserState({ membershipId: m.id, state: 'SUSPENDED' }), t('staff.suspended'))}
                >
                  {t('staff.suspend')}
                </button>
              </>
            )}
            {m.state === 'SUSPENDED' && (
              <button type="button" disabled={busy} onClick={() => void act(() => endpoints.setUserState({ membershipId: m.id, state: 'ACTIVE' }), t('staff.restored'))}>
                {t('staff.restore')}
              </button>
            )}
            {m.state !== 'REVOKED' && (
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => window.confirm(t('staff.confirmRevoke', { name })) && void act(() => endpoints.setUserState({ membershipId: m.id, state: 'REVOKED' }), t('staff.revoked'))}
              >
                {t('staff.revokeAccess')}
              </button>
            )}
          </div>
          <p className="field-hint">{t('staff.actionsHelp')}</p>
          {m.state === 'REVOKED' && <p className="field-hint">{t('staff.revokedHelp')}</p>}
        </section>
      )}
      {!canWrite && <p className="disabled-note">{t('staff.readOnly')}</p>}
    </div>
  );
}
